import { useEffect, useRef } from "react";

import { useDialogSurface } from "../../../hooks/useDialogSurface";
import { useFocusTrap } from "../../../hooks/useFocusTrap";
import {
  isTopmostDialogSurface,
  markEscapeConsumed,
} from "../../../lib/escapeConsumption";
import { Button } from "./Button";

/**
 * THE FACILITATOR OS DIALOG FRAME. Scrim, header, focus trap, Escape, and focus
 * returned. Promoted out of `pages/Admin/Manage/room/RoomDialog.jsx` (FE-9.2b).
 *
 * ===================== ONE FRAME BECAUSE THERE ARE SEVEN OF THEM ===========
 * `SquadCreateDialog` wrote this behaviour inline and got it right, including
 * the two traps below. FE-8 added an add dialog, a rename dialog, a move dialog,
 * a create sequence and a student drawer, and the frame absorbed all five. Two
 * more transcribed it by hand one directory over instead: `SquadArchiveDialog`
 * and `CommitOutcome` reproduced the scrim, the panel classes, the
 * borrow-and-return focus effect and the capture-phase Escape listener line for
 * line (duplication D13 in the audit). Both now render through this file, which
 * is also why it no longer lives under `Manage/`: a frame the whole OS is
 * supposed to use cannot sit inside one page's room directory.
 *
 * ===================== ESCAPE IS A PROP, NOT AN ASSUMPTION =================
 * `dismissible` defaults true and three consumers pass it false conditionally.
 * Two different reasons, and both are first-class:
 *
 *   THE SECRET. `CredentialPanel` and `CommitOutcome` pass `false` while an
 *   unacknowledged one-shot password is on screen. A panel carrying a value the
 *   server will never send again is a panel that must not close on a stray
 *   keypress.
 *
 *   THE WRITE THAT MAY HAVE COMMITTED (the M2/FE-8 dismissal lock).
 *   `AddStudentDialog` passes `!busy` and `StudentDrawer` passes
 *   `!mutations.busy`. While a mutation is in flight the client cannot know
 *   whether the server has already applied it, so dismissing would leave the
 *   teacher looking at a room that may or may not contain their change with no
 *   statement either way. The lock holds until the response resolves the
 *   question. It is stated here rather than left to each caller's memory
 *   because the failure is silent: a dialog that closes early looks like it
 *   worked.
 *
 * A non-dismissible panel does not merely ignore Escape. It SWALLOWS it, so the
 * keypress does not reach anything behind the frame either.
 *
 * ===================== ESCAPE OWNERSHIP IS DECLARED, NOT GUESSED ===========
 * The old form ran a capture-phase listener that consumed every Escape
 * unconditionally, which is correct exactly as long as this frame is the only
 * thing on screen. It is not: the facilitator assistant is mounted on all four
 * OS surfaces and is itself a dialog. So the frame registers as a dialog
 * surface (`useDialogSurface`), acts only when it is the one in front
 * (`isTopmostDialogSurface`), and marks the Escape it consumed so opt-in
 * listeners stand down. One Escape closes one thing. See the register's own
 * scope note in `lib/escapeConsumption.js`.
 *
 * ===================== THE HANDLER IS FRESH, THE EFFECT IS NOT =============
 * `onClose` is an inline arrow at every call site, so its identity changes on
 * every parent render, and parents re-render while a dialog is open (the busy
 * interval of a submit, and the authoritative re-read after it). An effect that
 * depended on it would tear down and re-run each time: restore focus to the
 * opener, re-capture the opener, and pull focus back into the panel, taking it
 * off whatever the teacher was typing in. The borrow-and-return of focus is a
 * mount/unmount fact, so it is keyed on nothing and reads the current handler
 * through a ref.
 *
 * ===================== SUCCESS IS NOT A SLOT HERE, AND THAT IS DELIBERATE ==
 * The frame owns the busy state (`DialogActions busy`) and the error state
 * (`DialogRefusal`). It does NOT own success, because the two live success
 * renders in the OS are not notices: the add-student outcome and the password
 * reset outcome both carry structure (what was written, a credential, a
 * recovery sentence) that a one-line status paragraph cannot hold, and
 * inventing a slot neither of them could use would be an abstraction against a
 * future nobody has asked for. Unifying those two is FE-9.4's, on the surface
 * that owns them.
 *
 * ===================== THE FRAME OWNS THE SCROLL, SO IT OWNS THE EXIT ======
 * `footer` is not a styling convenience. The panel caps at `max-h-[86vh]` and
 * scrolls, and this file is the only place that fact is written down, so a
 * dismissal control rendered as ordinary flow content scrolls away with the
 * content it is the exit FROM. On macOS the overlay scrollbar paints nothing,
 * so there is no cue that anything continues below the fold. Manage's dialogs
 * carry no scrim dismissal by design (earned divergence 2), which leaves
 * Escape as the only remaining door, and Escape is SWALLOWED whenever
 * `dismissible` is false. On `CommitOutcome` (`false` unconditionally) and
 * `CredentialPanel` (`false` for as long as a one-shot password is on screen)
 * that swallow is a steady state nothing but the close control ends, so a
 * close control below the fold there is a permanent lockout.
 *
 * Reported live on 2026-08-08 against `StudentDrawer` mid-move: content
 * measured 776px against a 740px box at 1440x863 and the close row was the
 * last node of the scroll. That drawer was fixed in place first (12d3148);
 * this is the same fix moved to the element that actually owns the cap, which
 * is what makes it true for all eleven mounts rather than one.
 *
 * A SLOT AND NOT A FRAME-RENDERED BUTTON. Eleven mounts end in eleven
 * different rows: two-control confirms, a single acknowledge whose label
 * changes with whether it is holding a secret, and a three-control next-steps
 * row. The frame supplying its own close would either duplicate those or
 * overrule them. It supplies the PLATE instead, and every caller's controls,
 * labels, order, disabled states and handlers pass through untouched.
 *
 * The plate is the panel's own `bg-background-cloudburst` token rather than a
 * hand-composited literal, so the two cannot drift apart: opacity here would
 * let scrolled rows ghost through, and a computed literal would go stale the
 * moment the panel's ground moved. The negative margins cancel the panel's own
 * `p-8` (the same file sets both, so they cannot disagree) and `rounded-b-xl`
 * follows the panel's `rounded-xl`. No gradient on the panel, no gradient
 * here: a plate is only opaque against a flat ground (P29).
 */
export const Dialog = ({
  label,
  title,
  titleTestId,
  subject,
  subjectTestId,
  description,
  testId,
  dismissible = true,
  onClose,
  initialFocusRef,
  children,
  footer,
  width = "480px",
}) => {
  const panelRef = useRef(null);

  useFocusTrap(panelRef, true);
  useDialogSurface(panelRef);

  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  /* WRITTEN THE SAME WAY `onCloseRef` IS, and for a stronger reason. Assigned
     during render, the value could leak out of render work React replays or
     discards, and the one thing this ref decides is whether Escape can dismiss
     a panel holding a credential or an in-flight write. The Escape handler only
     ever reads it from a committed event, so an effect is early enough. */
  const dismissibleRef = useRef(dismissible);
  useEffect(() => {
    dismissibleRef.current = dismissible;
  });

  useEffect(() => {
    const opener = document.activeElement;
    /* THE NAMED FIELD, OR THE PANEL. Written as a branch rather than as `a ??
       b`, because `focus()` returns undefined and undefined is nullish, so the
       coalescing form would ALWAYS run the fallback and pull focus straight
       back out of the field the caller asked for. */
    const initial = initialFocusRef?.current;
    if (initial instanceof HTMLElement) {
      initial.focus();
    } else {
      panelRef.current?.focus?.();
    }

    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      // Not in front, not this panel's Escape. Standing down rather than
      // swallowing is the whole point of the register: the surface on top gets
      // to close, and a second Escape reaches this one.
      if (!isTopmostDialogSurface(panelRef.current)) return;
      // Swallowed either way, dismissible or not.
      event.preventDefault();
      event.stopPropagation();
      markEscapeConsumed(event);
      if (dismissibleRef.current) onCloseRef.current?.();
    };
    document.addEventListener("keydown", onKeyDown, true);

    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      /* FOCUS IS BORROWED AND GIVEN BACK, WHEN THERE IS SOMETHING TO GIVE IT
         BACK TO. `<body>` is not an opener: restoring to it leaves a keyboard
         user with nothing focused, one Tab from the top of the document. */
      if (isRestorableOpener(opener)) opener.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {/* The scrim carries NO handler at all, which is the strongest form of
          "a click outside does not close". Dismissal is a control with a label
          on it. Earned divergence 2: Coach's read-only drawer closes on a scrim
          click and should; a panel holding a staged twenty-person plan or a
          one-shot credential must not. */}
      <div
        aria-hidden="true"
        data-testid={`${testId}-scrim`}
        className="fixed inset-0 z-40 bg-background-blackpearl/70"
      />
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        data-testid={testId}
        style={{ width: `min(92vw, ${width})` }}
        className="fixed left-1/2 top-1/2 z-50 max-h-[86vh] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-xl border border-white/[0.06] bg-background-cloudburst p-8 shadow-modal outline-none"
      >
        {/* ONE HEADER SHAPE. Seven dialogs each opened with the identical
            `<h3 className="text-md text-lv0">` and passed the same string to
            `label`, so the heading and the accessible name were maintained as
            two facts that happened to agree. `subject` is the thing being acted
            on (a squad name, a student name) and `description` is the sentence
            that explains the consequence before it happens. */}
        {title ? (
          <h3 className="text-md text-lv0" data-testid={titleTestId}>
            {title}
          </h3>
        ) : null}
        {subject ? (
          /* M12 (FE-9.8d, P26). THE SUBJECT IS A PRIMARY IDENTIFIER: it names
             the thing the dialog is about to act on, and a teacher confirms an
             irreversible act by reading it. 16px/600 is the floor every other
             scan target on the OS now takes, and this is one dialog-wide
             decision rather than a per-dialog override. */
          <p
            className="mt-2 text-md font-semibold text-lv0"
            data-testid={subjectTestId}
          >
            {subject}
          </p>
        ) : null}
        {description ? (
          <p className="mt-3 max-w-[52ch] text-sm text-lv3">{description}</p>
        ) : null}
        {children}
        {footer ? (
          /* A DIRECT CHILD OF THE SCROLLING PANEL, which is what makes
             `sticky` resolve against the panel box rather than against
             whatever a caller happened to wrap its controls in. */
          <div
            data-testid={`${testId}-footer`}
            /* `-bottom-8` AND NOT `bottom-0`, measured in review (2026-08-08).
               Sticky offsets resolve against the scrollport, which stops at
               the panel's `p-8`, so `bottom-0` seated the plate 33px above the
               panel's bottom edge and mid-scroll rows painted BELOW it in the
               band the negative margins reach into. Offsetting by the same
               token that names the padding seats the plate on the padding-box
               bottom: the only strip left under it is the 1px border, where
               overflow content cannot paint. */
            className="sticky -bottom-8 -mx-8 -mb-8 mt-6 rounded-b-xl border-t border-white/[0.06] bg-background-cloudburst px-8 py-4"
          >
            {footer}
          </div>
        ) : null}
      </div>
    </>
  );
};

/**
 * Is this captured opener worth giving focus back to?
 *
 * `document.body` IS NOT, and that exclusion is the whole point. In the roster
 * flow the commit control unmounts as the outcome renders, so by the time the
 * effect captures `document.activeElement` the answer is already `<body>`.
 * Restoring to it on close then undoes the deliberate hand-off to the file
 * picker: Chrome moves focus to the body element and a keyboard user is left
 * with nothing focused, one Tab away from the top of the document.
 *
 * jsdom DOES NOT MODEL THIS. `body.focus()` there is a no-op, so a DOM
 * assertion passes either way and cannot see the defect. That is exactly why
 * this predicate is exported and unit tested rather than left inline: the guard
 * is asserted where it can actually fail. It moved here from `CommitOutcome`
 * with the frame, because it is the frame that returns focus now.
 */
export const isRestorableOpener = (node) =>
  node instanceof HTMLElement &&
  node !== document.body &&
  document.contains(node);

/**
 * The two buttons every dialog ends with, so their order never drifts.
 *
 * SECONDARY LEFT, PRIMARY RIGHT. One order, stated once, rather than seven
 * footers that happen to agree.
 *
 * THE SUBMIT IS CHARLOTTE UNLESS THE CALLER SAYS OTHERWISE. Reversible confirms
 * take the ordinary primary fill, which is what leaves persimmon free to mean
 * consequence and nothing else. `variant="destructive"` is not a style choice
 * available to any caller who likes the colour: it is for a one-way door, and
 * it does two things rather than one.
 *
 * A DESTRUCTIVE SUBMIT IS ISOLATED, NOT MERELY RECOLOURED. It takes a wider gap
 * from the control beside it, so the irreversible act is not one mis-aimed
 * click away from cancel. Colour alone fails the teacher who is moving fast,
 * and it fails entirely for a teacher who cannot distinguish the two fills.
 *
 * IT CARRIES NO TOP MARGIN, because it is always the frame's `footer` now and
 * the frame's plate owns the separation from the content above it (a hairline
 * and its own padding). Keeping `mt-6` here would space the row off the
 * hairline INSIDE the plate, which is the one place the gap does not belong.
 */
export const DialogActions = ({
  cancelLabel,
  submitLabel,
  submitTestId,
  cancelTestId,
  submitVariant = "primary",
  cancelRef,
  submitRef,
  onCancel,
  onSubmit,
  busy,
  disabled,
}) => (
  <div
    className={`flex justify-end ${
      submitVariant === "destructive" ? "gap-8" : "gap-3"
    }`}
  >
    <Button
      ref={cancelRef}
      variant="tertiary"
      onClick={onCancel}
      disabled={busy}
      data-testid={cancelTestId}
    >
      {cancelLabel}
    </Button>
    <Button
      ref={submitRef}
      variant={submitVariant}
      onClick={onSubmit}
      loading={busy}
      disabled={disabled}
      data-testid={submitTestId}
    >
      {submitLabel}
    </Button>
  </div>
);

/**
 * A refusal, rendered as the server's own sentence, with the client's control
 * beside it when the client has one.
 *
 * The default is verbatim and the exceptions are enumerated in
 * `ROOM_REFUSAL_COPY`, for the two codes where the CLIENT has something the
 * server's sentence does not: which control to offer. Everything else,
 * including the three deliberately uninformative refusals, renders untouched.
 * Earned divergence 10.
 *
 * ===================== THE CONTROL HALF WAS DEAD UNTIL FE-9.4 ==============
 * `override` was accepted here and passed by nobody, and `ROOM_REFUSAL_COPY`
 * was exported and imported by nobody (audit M-38). So after an
 * `idempotency_conflict` on any of the room's dialog writes a teacher read the
 * server's sentence and was offered nothing, on a surface whose sibling
 * workspaces both offer a control for the same code. The dead end the whole
 * refusal grammar was built to remove survived in exactly the dialogs that did
 * not get the grammar.
 *
 * THE CLIENT STILL NEVER INVENTS RETRYABILITY. The control renders only when a
 * caller supplies BOTH an override carrying a label and a handler for it; a code
 * absent from the map, or a caller with nothing sensible to do, renders the
 * sentence alone exactly as before.
 */
export const DialogRefusal = ({
  refusal,
  override,
  testId,
  onControl,
  controlTestId,
}) => {
  if (!refusal) return null;
  const control = override?.control && onControl ? override.control : null;

  return (
    <div className="mt-4 flex flex-wrap items-center gap-3">
      <p role="status" data-testid={testId} className="text-sm text-lv2">
        {override?.message ?? refusal.message}
      </p>
      {control ? (
        <Button
          size="sm"
          variant="secondary"
          data-testid={controlTestId}
          onClick={onControl}
        >
          {control}
        </Button>
      ) : null}
    </div>
  );
};

export default Dialog;
